import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  notification,
} from 'antd'
import React, { useEffect } from 'react'
import { NOTIFICATION_DURATION, handleExportCSVScreen } from '~/common/constants'
import { useQueryState } from '~/common/hooks/useQueryState'
import { useTranslation } from '~/common/hooks/useTranslation'
import Datatable from '~/components/shared-components/Datatable'
import configs from '~/configs'
import { formatAddress, formatTransaction } from '~/helpers/common'
import TimeHelper from '~/helpers/time-helper'
import { usePools } from '~/views/app/redux/hooks/usePools'
import moment from 'moment'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useSDK } from '@metamask/sdk-react'
import { changeNetworkInMetamask } from '~/blockchain/metamask'
import { useAccount } from '~/common/hooks/useAccount'
import { checkTransactionConfirmation, hexToNumberString, numberToHex, toWei } from '~/blockchain/provider'
import { ifaceContractPool } from '~/blockchain/contract'
import { utils } from "ethers"

const { RangePicker } = DatePicker

dayjs.extend(utc)
dayjs.extend(timezone)

const onSuccess = (message) => {
  notification.success({
    message: message,
    duration: NOTIFICATION_DURATION,
  })
}

const RankingRewards = () => {
  const { t } = useTranslation()
  const { page, pageSize, keyword, startTime, endTime, setPage, setPageSize, setKeyword, setStartTime, setEndTime } = useQueryState()
  const [formDeposit] = Form.useForm()
  const [formWithdraw] = Form.useForm()

  const {
    actions,
    data: { isLoadingDeposit, isLoadingWithdraw, statisticData, histories, total },
  } = usePools()

  const { sdk } = useSDK()
  const { user } = useAccount()

  const userTimezone = dayjs.tz.guess()
  const userUtcOffset = dayjs().tz(userTimezone).utcOffset() / 60

  const toOAS = (value) => {
    return (Number(value) / 1e18).toFixed(6)
  }

  const columns = [
    {
      title: t('managementPools.rankingRewards.form.rewardType'),
      dataIndex: 'rewardType',
      key: 'rewardType',
      width: 100,
      align: 'center',
      render: (data) => {
        const formatted =
          typeof data === 'string'
            ? data.charAt(0) + data.slice(1).toLowerCase()
            : data
        return <div style={{ color: 'white' }}>{formatted}</div>
      },
    },
    {
      title: t('managementPools.rankingRewards.form.from'),
      dataIndex: 'from',
      key: 'from',
      width: 100,
      align: 'center',
      render: (from) => {
        let link = `${configs.NETWORK.scan}address/${from}`
        return (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={link}
            title={from}
            style={{ color: '#40A9FF' }}
          >
            {formatAddress(from)}
          </a>
        )
      },
    },
    {
      title: (
        <>
          {t('managementPools.rankingRewards.form.time')} (UTC{' '}
          {userUtcOffset >= 0 ? `+${userUtcOffset}` : userUtcOffset})
        </>
      ),
      dataIndex: 'time',
      key: 'time',
      width: 100,
      align: 'center',
      render: (time) => {
        return <div style={{ color: 'white' }}>{dayjs(time).format('YYYY-MM-DD HH:mm:ss')}</div>
      },
    },
    {
      title: <div style={{ paddingLeft: '60px' }}>{t('managementPools.rankingRewards.form.amount')}</div>,
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'center',
      render: (data) => {
        return <div style={{ color: 'white', paddingLeft: '60px' }}>{toOAS(data)} OAS</div>
      },
    },
    {
      title: t('managementPools.rankingRewards.form.txHash'),
      dataIndex: 'txHash',
      key: 'txHash',
      width: 100,
      align: 'center',
      render: (txHash) => {
        if (!txHash) return <div style={{ color: 'white' }}>-</div>
        const link = `${configs.NETWORK.scan}tx/${txHash}`
        return (
          <a
            style={{ color: '#40A9FF' }}
            href={link}
            title={txHash}
            target="_blank"
            rel="noopener noreferrer"
          >
            {formatTransaction(txHash)}
          </a>
        )
      },
    },
  ]

  const rules = {
    deposit: [
      {
        required: true,
        message: t('validate.required', {
          0: t('common.btn.deposit'),
        }),
      },
      {
        validator: (_, value) => {
          if (value === undefined || value === null || value === '')
            return Promise.resolve()
          const strValue = value.toString()
          if (!/^\d*\.?\d*$/.test(strValue)) {
            return Promise.reject(new Error(t('validate.noSpecialChars')))
          }
          if (Number(value) < 0) {
            return Promise.reject(new Error(t('validate.positiveNumber')))
          }
          const decimalPart = value.toString().split('.')[1]
          if (decimalPart && decimalPart.length > 6) {
            return Promise.reject(new Error(t('validate.max6Decimals')))
          }
          return Promise.resolve()
        },
      },
    ],
    withdraw: [
      {
        required: true,
        message: t('validate.required', {
          0: t('common.btn.withdraw'),
        }),
      },
      {
        validator: (_, value) => {
          if (value === undefined || value === null || value === '')
            return Promise.resolve()
          const strValue = value.toString()
          if (!/^\d*\.?\d*$/.test(strValue)) {
            return Promise.reject(new Error(t('validate.noSpecialChars')))
          }
          if (Number(value) < 0) {
            return Promise.reject(new Error(t('validate.positiveNumber')))
          }
          const decimalPart = value.toString().split('.')[1]
          if (decimalPart && decimalPart.length > 6) {
            return Promise.reject(new Error(t('validate.max6Decimals')))
          }
          const valueWei = utils.parseUnits(value, 18)
          const balanceWei = BigInt(statisticData?.currentBalance)
          if (valueWei > balanceWei) {
            return Promise.reject(new Error(t('validate.greaterThanTotal')))
          }
          return Promise.resolve()
        },
      },
    ],
  }

  useEffect(() => {
    actions.getRankingRewards({
      page,
      pageSize,
      search: keyword,
    })
    actions.getHistoryRankingRewards({
      page,
      pageSize,
      search: keyword,
      startTime,
      endTime
    })
  }, [page, pageSize, keyword, startTime, endTime])

  const sendContractTx = async (method, amount, setLoading, onTxSuccess) => {
    try {
      setLoading(true)
      await sdk.connect()
      await changeNetworkInMetamask(sdk.getProvider(), configs.NETWORK.chainId, setLoading)

      let networkId = await sdk.getProvider().request({ method: 'eth_chainId', params: [] })
      networkId = hexToNumberString(networkId)

      if (parseInt(networkId) !== parseInt(configs.NETWORK.chainId)) {
        setLoading(false)
        return
      }

      let nonce = await sdk.getProvider().request({
        method: 'eth_getTransactionCount',
        params: [user.address_wallet, 'latest'],
      })

      const txParams = {
        from: user.address_wallet,
        data: ifaceContractPool.encodeFunctionData(method, [toWei(amount)]),
        to: configs.ADDRESS_POOL,
        nonce,
      }

      if (method === 'deposit') {
        txParams.value = numberToHex(toWei(amount))
      }

      sdk.getProvider()
        .request({
          method: 'eth_sendTransaction',
          params: [txParams],
        })
        .then((txHash) => {
          checkTransactionConfirmation(txHash).then((r) => {
            if (r === 'confirmed') {
              console.log(r, txHash)
              setLoading(false)
              onTxSuccess()
            }
          })
        })
        .catch((error) => {
          console.error(error)
          setLoading(false)
        })
    } catch (error) {
      console.error(error)
      setLoading(false)
    }
  }

  const handleSubmitDeposit = async (values) => {
    const onTxSuccess = () => {
      onSuccess(t('common.message.depositSuccessfully'))
      formDeposit.resetFields()
      actions.getRankingRewards({ page, pageSize, search: keyword })
    }
    sendContractTx('deposit', values.deposit, () => {}, onTxSuccess)
  }

  const handleSubmitWithdraw = async (values) => {
    const onTxSuccess = () => {
      onSuccess(t('common.message.withdrawSuccessfully'))
      formWithdraw.resetFields()
      actions.getRankingRewards({ page, pageSize, search: keyword })
    }
    sendContractTx('withdraw', values.withdraw, () => {}, onTxSuccess)
  }

  const onChangeDate = (values) => {
    if (values && values.length === 2) {
      const [start, end] = values
      const startTime = start ? dayjs(start).utc().startOf('day').unix() : null
      const endTime = end ? dayjs(end).utc().add(1, 'day').endOf('day').unix() : null

      setStartTime(startTime)
      setEndTime(endTime)
      actions.getHistoryRankingRewards({
        page: 1,
        pageSize,
        search: keyword,
        startTime,
        endTime,
      })
      setPage(1)
    } else {
      setStartTime(null)
      setEndTime(null)
      actions.getHistoryRankingRewards({
        page: 1,
        pageSize,
        search: keyword,
      })
      setPage(1)
    }
  }

  const paramsExportHistory = {
    startTime,
    endTime,
  }

  return (
    <div>
      <Row gutter={10}>
        <Col xl={16} md={16} sm={24} xs={24} className="h-full">
          <div
            className="relative p-[20px] h-[260px]"
            style={{
              background:
                'linear-gradient(90deg, rgba(24, 144, 255, 0.4) 0%, rgba(24, 144, 255, 0) 100%)',
              borderRadius: 10,
              marginBottom: 10,
            }}
          >
            <Row gutter={10}>
              <Col xl={8} md={8} sm={24} xs={24}>
                <div className="h-full flex flex-col justify-center items-center">
                  <img
                    src={`${process.env.PUBLIC_URL}/imgs/common/gift.png`}
                    width={209}
                    height={166}
                    alt=""
                  />
                </div>
              </Col>
              <Col xl={16} md={16} sm={24} xs={24}>
                <div className="grid grid-cols-1 sm:grid-cols-2 h-full min-h-[164px]">
                  <div className="h-full flex flex-col justify-center">
                    <div className="text-[16px] font-bold mb-1">
                      {t('managementPools.rankingRewards.totalPools')}:
                    </div>
                    <div className="text-[24px] text-[#40A9FF] font-bold">
                      {toOAS(statisticData?.currentBalance || 0)} OAS
                    </div>
                  </div>
                  <div className="h-full flex flex-col justify-center">
                    <div className="text-[16px] font-bold mb-1">
                      {t('managementPools.rankingRewards.requiredForClaiming')}:
                    </div>
                    <div className="text-[24px] text-[#40A9FF] font-bold">
                      {toOAS(statisticData?.totalNeeded || 0)} OAS
                    </div>
                  </div>
                  <div className="h-full flex flex-col justify-center mt-2">
                    <div className="text-[16px] font-bold mb-1">
                      {t('managementPools.rankingRewards.claimed')}:
                    </div>
                    <div className="text-[24px] text-[#40A9FF] font-bold">
                      {toOAS(statisticData?.totalClaimed || 0)} OAS
                    </div>
                  </div>
                  <div className="h-full flex flex-col justify-center mt-2">
                    <div className="text-[16px] font-bold mb-1">
                      {t('managementPools.rankingRewards.requiredDeposit')}:
                    </div>
                    <div className="text-[24px] text-[#FF4D4F] font-bold">
                      {toOAS(statisticData?.shortage || 0)} OAS
                    </div>
                  </div>
                  <div className="h-full flex flex-col justify-center mt-2">
                    <div className="text-[16px] font-bold mb-1">
                      {t('managementPools.rankingRewards.totalOverdueAmount')}:
                    </div>
                    <div className="text-[24px] text-[#40A9FF] font-bold">
                      {toOAS(statisticData?.totalExpired || 0)} OAS
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
        <Col xl={8} md={8} sm={24} xs={24}>
          <Row gutter={10} className="h-[143px]">
            <Col xl={24} md={24} sm={24} xs={24}>
              <Card className="h-[260px] flex flex-col justify-center">
                <div className="mb-3 text-[14px]">
                  <span className="font-bold mr-2 text-white">Pool Contract:</span>
                  <a
                    href={`${configs.NETWORK.scan}address/${configs.ADDRESS_POOL}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#40A9FF]"
                  >
                    {formatAddress(configs.ADDRESS_POOL)}
                  </a>
                </div>
                <Form
                  layout="vertical"
                  onFinish={handleSubmitDeposit}
                  form={formDeposit}
                  translate="yes"
                  autoComplete="off"
                  size="middle"
                  validateTrigger={['onBlur', 'onChange']}
                >
                  <Row gutter={10}>
                    <Col xl={15} md={15} sm={24} xs={24}>
                      <Form.Item
                        label={t('common.btn.deposit')}
                        name="deposit"
                        rules={rules.deposit}
                        labelAlign="left"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xl={9} md={9} sm={24} xs={24}>
                      <div className="flex flex-col">
                        <div className="h-[30px] hidden sm:block"></div>
                        <Button
                          disabled={isLoadingDeposit}
                          loading={isLoadingDeposit}
                          htmlType="submit"
                          type="primary"
                        >
                          {t('common.btn.deposit')}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
                <Form
                  layout="vertical"
                  onFinish={handleSubmitWithdraw}
                  form={formWithdraw}
                  translate="yes"
                  autoComplete="off"
                  size="middle"
                  validateTrigger={['onBlur', 'onChange']}
                >
                  <Row gutter={10}>
                    <Col xl={15} md={15} sm={24} xs={24}>
                      <Form.Item
                        label={t('common.btn.withdraw')}
                        name="withdraw"
                        rules={rules.withdraw}
                        labelAlign="left"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xl={9} md={9} sm={24} xs={24}>
                      <div className="flex flex-col">
                        <div className="h-[30px] hidden sm:block"></div>
                        <Button
                          disabled={isLoadingWithdraw}
                          loading={isLoadingWithdraw}
                          htmlType="submit"
                          type="primary"
                        >
                          {t('common.btn.withdraw')}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col span={12} className="h-full box__filter mb-3 mt-6">
          <div className="flex items-center">
            <div className="mr-2 font__Rexliafree font-bold mt-2">
              {t('managementPools.rankingRewards.history')}
            </div>
          </div>
        </Col>
        <Col span={12} className="h-full box__filter mb-3 mt-6">
          <div className="flex items-center justify-end">
            <div className="mr-2 font__Rexliafree font-bold">
              {t('historyTrade.form.date')}
            </div>
            <RangePicker
              onChange={onChangeDate}
              className="h-full max-w-[256px]"
            />
          </div>
        </Col>
        <Col span={24} className="h-full">
          <Datatable
            scroll={{ x: 1300 }}
            columns={columns}
            dataSource={histories}
            showPagination
            showHeader
            loading={false}
            leftFooter={
              <Button
                style={{ backgroundColor: '#1890FF' }}
                onClick={() =>
                  handleExportCSVScreen(
                    'RANKING_REWARDS_HISTORY',
                    paramsExportHistory,
                    'Ranking_Rewards_History',
                  )
                }
              >
                Export to CSV
              </Button>
            }
            metadata={{
              perPage: pageSize,
              setPerPage: setPageSize,
              page,
              setPage,
              total: total,
              keyword,
              setKeyword,
            }}
          />
        </Col>
      </Row>
    </div>
  )
}

export default RankingRewards